import React, { useEffect, useMemo, useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { AlertCircle, CheckCircle2, Loader2, User, Building2, ShieldCheck, Lock, Sparkles } from "lucide-react";
import { COUNTRIES, findCountryByCode, DEFAULT_COUNTRY } from "../data/countries";
import { getGeoLocation, submitLead, redirectToApplication } from "../services/api";

const WIKI_TYPES = [
  { value: "individual", label: "Individual Page", icon: User },
  { value: "company", label: "Company Page", icon: Building2 },
];

/**
 * SignupForm — the unified conversion form used everywhere
 * (popup dialog, hero section, landing page, etc.).
 * Design: white card with black + orange brand banner as per spec.
 */
export const SignupForm = ({ compact = false, testIdPrefix = "signup" }) => {
  const [values, setValues] = useState({
    name: "",
    email: "",
    countryCode: DEFAULT_COUNTRY?.code || "US",
    phone: "",
    wikiType: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const country = useMemo(
    () => findCountryByCode(values.countryCode) || DEFAULT_COUNTRY,
    [values.countryCode]
  );

  useEffect(() => {
    let active = true;
    getGeoLocation().then((geo) => {
      if (!active || !geo?.data?.countryCode) return;
      const match = findCountryByCode(geo.data.countryCode);
      if (match) {
        setValues((v) =>
          v.countryCode === (DEFAULT_COUNTRY?.code || "US")
            ? { ...v, countryCode: match.code }
            : v
        );
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const set = (k) => (e) => {
    const val = typeof e === "string" ? e : e?.target?.value ?? "";
    setValues((p) => ({ ...p, [k]: val }));
  };
  const onPhone = (e) =>
    setValues((p) => ({
      ...p,
      phone: (e.target.value || "").replace(/\D+/g, "").slice(0, 10),
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!values.name.trim()) return setError("Please enter your name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
      return setError("Please enter a valid email address.");
    if (!values.phone || values.phone.length !== 10)
      return setError("Please enter a valid 10-digit phone number.");
    if (!values.wikiType)
      return setError("Please select the type of Wikipedia page you need.");

    setSubmitting(true);
    const res = await submitLead({
      name: values.name,
      email: values.email,
      countryDialCode: country.dial,
      country: country.code,
      phoneNumber: values.phone,
      description: `Wiki Page Type: ${
        WIKI_TYPES.find((t) => t.value === values.wikiType)?.label
      }`,
    });
    setSubmitting(false);

    if (res.ok) {
      setSuccess("Success! Redirecting...");
      setTimeout(() => redirectToApplication(res.customerId), 600);
      return;
    }
    setError(res.message || "Submission failed. Please try again.");
  };

  return (
    <div
      className={`relative w-full overflow-hidden rounded-2xl bg-white ${
        compact ? "p-4 sm:p-7" : "p-5 sm:p-9"
      } shadow-[0_30px_80px_-30px_rgba(10,37,64,0.45)] ring-1 ring-slate-100`}
      data-testid={`${testIdPrefix}-form-card`}
    >
      {/* Decorative top gradient */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#0A2540] via-[#0e2c4a] to-[#0A2540]" />

      {/* Title block — compact on mobile */}
      <div className="mb-3 text-center sm:mb-5">
        <div className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-[#0A2540]/10 bg-[#0A2540]/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#0A2540]">
          <Sparkles className="h-3 w-3" /> Limited · 35% off this week
        </div>
        <div className="font-serif text-base font-bold uppercase tracking-tight text-slate-900 sm:mt-3 sm:text-[1.4rem]">
          Submit This Form To
        </div>
        <div
          className="mt-1.5 inline-block rounded-lg px-4 py-1.5 font-serif text-sm font-bold uppercase tracking-tight text-white sm:mt-2 sm:rounded-xl sm:px-5 sm:py-2 sm:text-xl"
          style={{
            background: "linear-gradient(180deg, #0A2540 0%, #0e2c4a 60%, #0A2540 100%)",
            boxShadow: "0 10px 25px -10px rgba(10,37,64,0.65)",
          }}
        >
          Reserve Your Discount
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-2.5 sm:space-y-4" data-testid={`${testIdPrefix}-form`}>
        <div className="grid gap-2.5 sm:grid-cols-2 sm:gap-3">
          <div>
            <label htmlFor={`${testIdPrefix}-name`} className="mb-1 block text-xs font-semibold text-slate-700 sm:mb-1.5">Full name</label>
            <Input
              id={`${testIdPrefix}-name`}
              placeholder="Full name"
              value={values.name}
              onChange={set("name")}
              disabled={submitting}
              required
              className="h-10 rounded-lg border-slate-300 bg-slate-50/50 focus-visible:ring-emerald-400 sm:h-11"
              data-testid={`${testIdPrefix}-name-input`}
            />
          </div>
          <div>
            <label htmlFor={`${testIdPrefix}-email`} className="mb-1 block text-xs font-semibold text-slate-700 sm:mb-1.5">Work email</label>
            <Input
              id={`${testIdPrefix}-email`}
              type="email"
              placeholder="you@company.com"
              value={values.email}
              onChange={set("email")}
              disabled={submitting}
              required
              className="h-10 rounded-lg border-slate-300 bg-slate-50/50 focus-visible:ring-emerald-400 sm:h-11"
              data-testid={`${testIdPrefix}-email-input`}
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-700 sm:mb-1.5">Phone number</label>
          <div className="flex gap-2">
            <Select
              value={values.countryCode}
              onValueChange={set("countryCode")}
              disabled={submitting}
            >
              <SelectTrigger
                className="h-10 w-[110px] shrink-0 rounded-lg border-slate-300 bg-slate-50/50 sm:h-11 sm:w-[124px]"
                data-testid={`${testIdPrefix}-country-select`}
              >
                <SelectValue>
                  <span className="inline-flex items-center gap-2">
                    <span className="text-lg leading-none">{country?.flag}</span>
                    <span className="text-sm font-semibold">+{country?.dial}</span>
                  </span>
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    <span className="inline-flex items-center gap-2">
                      <span className="text-base">{c.flag}</span>
                      <span className="text-sm">{c.name}</span>
                      <span className="text-xs text-slate-500">+{c.dial}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              inputMode="numeric"
              placeholder="10-digit phone number"
              value={values.phone}
              onChange={onPhone}
              disabled={submitting}
              required
              maxLength={10}
              pattern="\d{10}"
              className="h-10 flex-1 rounded-lg border-slate-300 bg-slate-50/50 focus-visible:ring-emerald-400 sm:h-11"
              data-testid={`${testIdPrefix}-phone-input`}
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-700 sm:mb-1.5">Wikipedia page type</label>
          <div className="grid grid-cols-2 gap-2" role="radiogroup">
            {WIKI_TYPES.map((t) => {
              const checked = values.wikiType === t.value;
              return (
                <button
                  type="button"
                  key={t.value}
                  role="radio"
                  aria-checked={checked}
                  disabled={submitting}
                  onClick={() => setValues((p) => ({ ...p, wikiType: t.value }))}
                  className={`group flex items-center gap-1.5 rounded-lg border-2 px-2 py-2 text-left text-xs transition-all sm:gap-2 sm:px-3 sm:py-3 sm:text-sm ${
                    checked
                      ? "border-[#0A2540] bg-[#0A2540]/5 text-slate-900 shadow-[0_6px_18px_-8px_rgba(10,37,64,0.45)]"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                  data-testid={`${testIdPrefix}-wikitype-${t.value}`}
                >
                  <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-md sm:h-8 sm:w-8 ${checked ? "bg-[#0A2540] text-white" : "bg-slate-100 text-slate-500 group-hover:bg-slate-200"}`}>
                    <t.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </span>
                  <span className="flex-1 font-semibold leading-tight">{t.label}</span>
                  <span className={`hidden h-4 w-4 shrink-0 place-items-center rounded-full border-2 sm:grid ${checked ? "border-[#0A2540] bg-[#0A2540]" : "border-slate-300 bg-white"}`}>
                    {checked ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {error ? (
          <div
            className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            data-testid={`${testIdPrefix}-error`}
          >
            <AlertCircle className="mt-0.5 h-4 w-4" /> {error}
          </div>
        ) : null}
        {success ? (
          <div
            className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
            data-testid={`${testIdPrefix}-success`}
          >
            <CheckCircle2 className="mt-0.5 h-4 w-4" /> {success}
          </div>
        ) : null}

        <Button
          type="submit"
          disabled={submitting}
          className="h-11 w-full rounded-full px-6 text-sm font-semibold text-white shadow-[0_10px_30px_-10px_rgba(16,185,129,0.6)] transition-transform hover:scale-[1.01] hover:brightness-110 sm:h-12"
          style={{
            background: "linear-gradient(135deg, #0A2540 0%, #10B981 170%)",
          }}
          data-testid={`${testIdPrefix}-submit-button`}
        >
          {submitting ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
            </span>
          ) : (
            "Get Started →"
          )}
        </Button>

        {/* Trust strip — hidden on smallest screens to keep form in-viewport */}
        <div className="hidden items-center justify-center gap-4 pt-1 text-[11px] text-slate-500 sm:flex">
          <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> 256-bit secure</span>
          <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> NDA-protected</span>
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> No spam, ever</span>
        </div>
      </form>
    </div>
  );
};

export default SignupForm;
